import {
  BASE_MOCK_CONFIG,
  type BaseMockConfig,
  knownIds,
  parseMockConfig as parseBaseConfig,
} from '@repo/utils/devTools';

/** How many rows a mock list holds; "default" is the fixed sample the mock always returned. */
export const LIST_SIZES = [
  { id: 'default', label: 'Muestra fija (pocos)' },
  { id: 'empty', label: 'Vacío' },
  { id: 'many', label: 'Muchos (30)' },
  { id: 'long', label: 'Nombres e importes largos' },
] as const;

export type ListSize = (typeof LIST_SIZES)[number]['id'];

/** The mock lists, each with its own size. */
export const LISTS = [
  { id: 'pendingOrders', label: 'Ventas en curso' },
  { id: 'successfulOrders', label: 'Ventas realizadas' },
  { id: 'canceledOrders', label: 'Ventas canceladas' },
  { id: 'customerCredits', label: 'Operaciones del cliente (Mis clientes)' },
  { id: 'settlements', label: 'Liquidaciones' },
  { id: 'invoices', label: 'Comprobantes' },
] as const;

export type ListId = (typeof LISTS)[number]['id'];

export const POLICY_SCENARIOS = [
  { id: 'default', label: 'Cuatro tipos de venta' },
  { id: 'single', label: 'Un solo tipo de venta' },
  { id: 'empty', label: 'Ninguno (pantalla de error)' },
] as const;

export const DNI_SCENARIOS = [
  { id: 'default', label: 'Dos personas con ese DNI' },
  { id: 'one', label: 'Una sola persona' },
  { id: 'notFound', label: 'DNI no encontrado' },
] as const;

export const RATING_SCENARIOS = [
  { id: 'approved', label: 'Aprobado (hasta $ 250.000)' },
  { id: 'campaign', label: 'Aprobado con mensaje de campaña' },
  { id: 'rejected', label: 'Rechazado, sin oferta' },
  { id: 'rejectedBnpl', label: 'Rechazado, con segunda chance BNPL' },
  { id: 'invalidPlan', label: 'Plan inválido' },
] as const;

export const DEADLINE_SCENARIOS = [
  { id: 'default', label: 'Tres planes (con y sin interés)' },
  { id: 'single', label: 'Un solo plan' },
  { id: 'none', label: 'Sin cuotas para el monto' },
] as const;

export const CREATE_ORDER_SCENARIOS = [
  { id: 'success', label: 'Orden creada' },
  { id: 'error', label: 'Error al crear la orden' },
] as const;

export const ORDER_STATUS_SCENARIOS = [
  { id: 'cancelOtp', label: 'Cancelada (intentos de SMS agotados)' },
  { id: 'cancelRisk', label: 'Cancelada (rechazo por riesgo)' },
  { id: 'pendingStart', label: 'En curso, recién iniciada' },
  { id: 'pendingAdvanced', label: 'En curso, a punto de confirmar' },
  { id: 'successful', label: 'Concretada' },
] as const;

export const SMS_SCENARIOS = [
  { id: 'sent', label: 'Enviado' },
  { id: 'failed', label: 'No se pudo enviar' },
] as const;

type Ids<T extends readonly { id: string }[]> = T[number]['id'];

export type MockConfig = BaseMockConfig &
  Record<ListId, ListSize> & {
    policies: Ids<typeof POLICY_SCENARIOS>;
    dniNames: Ids<typeof DNI_SCENARIOS>;
    rating: Ids<typeof RATING_SCENARIOS>;
    deadlines: Ids<typeof DEADLINE_SCENARIOS>;
    createOrder: Ids<typeof CREATE_ORDER_SCENARIOS>;
    orderStatus: Ids<typeof ORDER_STATUS_SCENARIOS>;
    sms: Ids<typeof SMS_SCENARIOS>;
  };

// Every first option reproduces what the mock routes answered before the dev tools existed
export const DEFAULT_MOCK_CONFIG: MockConfig = {
  ...BASE_MOCK_CONFIG,
  pendingOrders: 'default',
  successfulOrders: 'default',
  canceledOrders: 'default',
  customerCredits: 'default',
  settlements: 'default',
  invoices: 'default',
  policies: 'default',
  dniNames: 'default',
  rating: 'approved',
  deadlines: 'default',
  createOrder: 'success',
  orderStatus: 'cancelOtp',
  sms: 'sent',
};

// The picked option, or the default when an older cookie names one that no longer exists
const known = <Id extends string>(options: readonly { id: Id }[], value: unknown, fallback: Id): Id =>
  knownIds(options, [value])[0] ?? fallback;

const SCENARIO_OPTIONS = {
  policies: POLICY_SCENARIOS,
  dniNames: DNI_SCENARIOS,
  rating: RATING_SCENARIOS,
  deadlines: DEADLINE_SCENARIOS,
  createOrder: CREATE_ORDER_SCENARIOS,
  orderStatus: ORDER_STATUS_SCENARIOS,
  sms: SMS_SCENARIOS,
} as const;

const sanitize = (config: MockConfig): MockConfig => {
  const lists = Object.fromEntries(
    LISTS.map(({ id }) => [id, known(LIST_SIZES, config[id], DEFAULT_MOCK_CONFIG[id])]),
  ) as Record<ListId, ListSize>;
  const scenarios = Object.fromEntries(
    Object.entries(SCENARIO_OPTIONS).map(([key, options]) => {
      const field = key as keyof typeof SCENARIO_OPTIONS;
      return [field, known<string>(options, config[field], DEFAULT_MOCK_CONFIG[field])];
    }),
  ) as Pick<MockConfig, keyof typeof SCENARIO_OPTIONS>;
  return { ...config, ...lists, ...scenarios };
};

export const parseMockConfig = (value?: string | null): MockConfig =>
  parseBaseConfig(value, DEFAULT_MOCK_CONFIG, sanitize);
